import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import LocuLogo from '../components/LocuLogo';
import SEO from '../components/SEO';

const Privacy = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Privacy Policy | LOCU"
        description="Read LOCU's Privacy Policy. Learn how we collect, use, and protect your personal data including rent payment information and location data."
        path="/privacy"
      />
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Link to="/" className="flex items-center gap-2">
            <LocuLogo showText={true} textSize="text-xl" textColor="text-gray-900" />
          </Link>
          <Link
            to="/"
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back to Home
          </Link>
        </div>

        <div className="prose prose-lg max-w-none">
          <h1 className="text-4xl font-bold text-gray-900 mb-8">Privacy Policy</h1>
          <p className="text-gray-500 mb-8">Last updated: January 2025</p>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introduction</h2>
            <p className="text-gray-700 mb-4">
              LOCU ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy 
              explains how we collect, use, disclose, and safeguard your information when you use our 
              website and mobile application.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Information We Collect</h2>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">2.1 Personal Information</h3>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>Name, email address, and phone number</li>
              <li>Date of birth and gender</li>
              <li>Profile photo (optional)</li>
              <li>Professional information (optional)</li>
              <li>Interests and preferences (optional)</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">2.2 Property Information</h3>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>Rental property address</li>
              <li>Monthly rent amount</li>
              <li>Landlord details (name, contact)</li>
              <li>Rent agreement documents</li>
              <li>Payment receipts and transaction history</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">2.3 Usage Information</h3>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>Device information (type, OS, browser)</li>
              <li>IP address and location data</li>
              <li>App usage patterns and preferences</li>
              <li>Transaction history and points earned</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. How We Use Your Information</h2>
            <p className="text-gray-700 mb-4">
              We use your information to:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>Provide and maintain our services</li>
              <li>Verify rent payments and award LOCU Points</li>
              <li>Process transactions at partner restaurants</li>
              <li>Communicate with you about your account and rewards</li>
              <li>Improve our services and user experience</li>
              <li>Detect and prevent fraud or abuse</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Information Sharing</h2>
            <p className="text-gray-700 mb-4">
              We do not sell your personal information. We may share your information with:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>
                <strong>Partner Restaurants:</strong> Limited information (name, phone) to process 
                redemptions and reservations
              </li>
              <li>
                <strong>Service Providers:</strong> Trusted third parties who assist in operating 
                our platform (payment processors, cloud storage, analytics)
              </li>
              <li>
                <strong>Legal Authorities:</strong> When required by law or to protect our rights
              </li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Data Security</h2>
            <p className="text-gray-700 mb-4">
              We implement appropriate technical and organizational measures to protect your data:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>256-bit SSL encryption for data transmission</li>
              <li>Secure cloud infrastructure with regular security audits</li>
              <li>Access controls and authentication requirements</li>
              <li>Regular security assessments and penetration testing</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Your Rights</h2>
            <p className="text-gray-700 mb-4">
              You have the right to:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>Access and review your personal information</li>
              <li>Correct inaccurate or incomplete information</li>
              <li>Request deletion of your account and data</li>
              <li>Opt-out of marketing communications</li>
              <li>Download a copy of your data</li>
            </ul>
            <p className="text-gray-700 mb-4">
              To exercise these rights, contact us at{' '}
              <a href="mailto:privacy@locu.in" className="text-primary-600 hover:underline">
                privacy@locu.in
              </a>
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Data Retention</h2>
            <p className="text-gray-700 mb-4">
              We retain your information for as long as your account is active or as needed to provide 
              you services. If you delete your account, we will delete or anonymize your personal 
              information within 30 days, except where retention is required by law.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Cookies and Tracking</h2>
            <p className="text-gray-700 mb-4">
              We use cookies and similar technologies to enhance your experience, analyze usage, 
              and deliver personalized content. You can manage cookie preferences through your 
              browser settings.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Children's Privacy</h2>
            <p className="text-gray-700 mb-4">
              Our services are not intended for individuals under 18 years of age. We do not 
              knowingly collect personal information from children.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Changes to This Policy</h2>
            <p className="text-gray-700 mb-4">
              We may update this Privacy Policy periodically. We will notify you of significant 
              changes via email or through the app. The updated policy will be effective immediately 
              upon posting.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Contact Us</h2>
            <p className="text-gray-700 mb-4">
              If you have questions or concerns about this Privacy Policy, please contact us:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>Email: <a href="mailto:privacy@locu.in" className="text-primary-600 hover:underline">privacy@locu.in</a></li>
              <li>Address: LOCU Technologies Pvt Ltd, Bangalore, India</li>
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
